/**
 * Date-range slicer over the ordered snapshot frames.
 *
 * The page carries two time controls and they mean different things:
 *   - this RANGE picks which frames the time-series chart draws;
 *   - the "as at" chips pick the ONE date every point-in-time figure is
 *     computed at.
 * Overloading a single control with both meanings is exactly what this
 * component exists to avoid, so it is deliberately dumb: indices in, indices
 * out. It never moves the selected date and never states what the range is
 * used for — the page owns that copy, because the page is what knows.
 *
 * The value space is the INDEX of a frame in the ordered period list, not a
 * date. Frames are irregularly spaced (Aug 2024, Mar 2025, Jun 2025 …), and
 * stepping by index keeps every frame equally reachable from the keyboard
 * instead of making the crowded end of the timeline harder to hit than the
 * sparse end.
 *
 * Keyboard, on a focused handle:
 *   ArrowLeft / ArrowDown    one frame earlier
 *   ArrowRight / ArrowUp     one frame later
 *   PageDown / PageUp        a quarter of the timeline, minimum two frames
 *   Home / End               the earliest / latest frame that handle may take
 * Handles may meet (a one-frame range) but never cross.
 */
import React, { useCallback, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Def, fmtDay, type DefLookup, type WorkforcePeriod } from './apiContract';

export interface RangeSlicerProps {
  /** Ordered oldest → newest — the same list the period chips render. */
  periods: WorkforcePeriod[];
  startIndex: number;
  endIndex: number;
  /** Fires with clamped, non-crossing indices, and only on a real change. */
  onChange: (startIndex: number, endIndex: number) => void;
  /**
   * Where the single "as at" date sits on the same axis, so the two controls
   * are visibly related. Presentational only — the slicer never moves it.
   */
  asAtIndex?: number | null;
  def: DefLookup;
  disabled?: boolean;
  className?: string;
}

type Handle = 'start' | 'end';

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

const frameLabel = (p: WorkforcePeriod | undefined) =>
  !p ? '—' : p.is_live ? 'Today' : p.label || fmtDay(p.as_at);

export const RangeSlicer: React.FC<RangeSlicerProps> = ({
  periods,
  startIndex,
  endIndex,
  onChange,
  asAtIndex,
  def,
  disabled,
  className,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Handle | null>(null);

  const count = periods.length;
  const last = count - 1;
  const start = clamp(startIndex, 0, Math.max(0, last));
  const end = clamp(endIndex, start, Math.max(0, last));
  const isFull = start === 0 && end === last;
  const shown = end - start + 1;

  const pct = useCallback((i: number) => (last > 0 ? (i / last) * 100 : 0), [last]);

  const move = useCallback(
    (which: Handle, to: number) => {
      if (disabled || last < 0) return;
      if (which === 'start') {
        const next = clamp(to, 0, end);
        if (next !== start) onChange(next, end);
      } else {
        const next = clamp(to, start, last);
        if (next !== end) onChange(start, next);
      }
    },
    [disabled, end, last, onChange, start],
  );

  const indexFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el || last <= 0) return 0;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return 0;
      return clamp(Math.round(((clientX - rect.left) / rect.width) * last), 0, last);
    },
    [last],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || last <= 0) return;
    const el = trackRef.current;
    if (!el) return;
    const target = e.target as HTMLElement | null;
    const grabbed = (target?.closest('[data-handle]') as HTMLElement | null)?.dataset
      .handle as Handle | undefined;
    const idx = indexFromClientX(e.clientX);
    // A grabbed handle wins; otherwise the pointer pulls whichever handle it is
    // nearer to, so a press outside the current span always widens it.
    const which: Handle =
      grabbed ??
      (idx <= start ? 'start' : idx >= end ? 'end' : idx - start <= end - idx ? 'start' : 'end');
    dragRef.current = which;
    el.setPointerCapture(e.pointerId);
    move(which, idx);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    move(dragRef.current, indexFromClientX(e.clientX));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    const el = trackRef.current;
    if (el && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
  };

  const onKeyDown = (which: Handle) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || last <= 0) return;
    const cur = which === 'start' ? start : end;
    const page = Math.max(2, Math.round(count / 4));
    let next: number;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = cur - 1;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = cur + 1;
        break;
      case 'PageDown':
        next = cur - page;
        break;
      case 'PageUp':
        next = cur + page;
        break;
      case 'Home':
        next = which === 'start' ? 0 : start;
        break;
      case 'End':
        next = which === 'start' ? end : last;
        break;
      default:
        return;
    }
    e.preventDefault();
    move(which, next);
  };

  const renderHandle = (which: Handle) => {
    const i = which === 'start' ? start : end;
    const lo = which === 'start' ? 0 : start;
    const hi = which === 'start' ? end : last;
    return (
      <button
        type="button"
        data-handle={which}
        role="slider"
        aria-label={which === 'start' ? 'Range start frame' : 'Range end frame'}
        aria-orientation="horizontal"
        aria-valuemin={lo}
        aria-valuemax={hi}
        aria-valuenow={i}
        aria-valuetext={frameLabel(periods[i])}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onKeyDown={onKeyDown(which)}
        title={`${which === 'start' ? 'From' : 'To'} ${frameLabel(periods[i])} (${fmtDay(
          periods[i]?.as_at,
        )})`}
        className={cn(
          'absolute top-1/2 z-10 h-[15px] w-[15px] -translate-x-1/2 -translate-y-1/2 rounded-full',
          'border-2 border-heritage-blue bg-white shadow-sm',
          'transition-[left] duration-150 ease-out motion-reduce:transition-none',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-future-blue',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-ew-resize hover:border-future-blue',
        )}
        style={{ left: `${pct(i)}%` }}
      />
    );
  };

  return (
    <div className={cn('min-w-[240px]', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">
          Range
          <Def
            def={def}
            k="__range__"
            text="Narrows the time-series chart to the frames between the two handles. It changes nothing else: every tile and every point-in-time panel is still computed at the single 'as at' date, which is always kept inside this range."
          />
        </span>
        <button
          type="button"
          onClick={() => onChange(0, Math.max(0, last))}
          disabled={disabled || isFull || last < 0}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-future-blue',
            isFull || disabled
              ? 'cursor-default border-gray-100 text-gray-300'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50',
          )}
        >
          <RotateCcw className="h-3 w-3" />
          Full range
        </button>
      </div>

      <div className="mx-[8px] mt-2">
        <div
          ref={trackRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className={cn(
            'relative h-[22px] touch-none select-none',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          {/* rail */}
          <span className="absolute inset-x-0 top-1/2 h-[5px] -translate-y-1/2 rounded-full bg-gray-200" />
          {/* selected span */}
          <span
            className="absolute top-1/2 h-[5px] -translate-y-1/2 rounded-full bg-heritage-blue transition-[left,width] duration-150 ease-out motion-reduce:transition-none"
            style={{ left: `${pct(start)}%`, width: `${Math.max(0, pct(end) - pct(start))}%` }}
          />
          {/* one tick per frame */}
          {periods.map((p, i) => (
            <span
              key={p.as_at}
              aria-hidden
              className={cn(
                'absolute top-1/2 h-[9px] w-[1.5px] -translate-x-1/2 -translate-y-1/2 rounded-sm',
                i >= start && i <= end ? 'bg-white/70' : 'bg-gray-300',
              )}
              style={{ left: `${pct(i)}%` }}
            />
          ))}
          {/* where the single "as at" date sits on the same axis */}
          {asAtIndex !== null && asAtIndex !== undefined && asAtIndex >= 0 && asAtIndex <= last ? (
            <span
              aria-hidden
              title={`"As at" ${frameLabel(periods[asAtIndex])}`}
              className="absolute top-0 h-[6px] w-[2px] -translate-x-1/2 rounded-sm bg-future-blue"
              style={{ left: `${pct(asAtIndex)}%` }}
            />
          ) : null}
          {renderHandle('start')}
          {renderHandle('end')}
        </div>
      </div>

      <div className="mt-1.5 text-[11.5px] leading-snug text-gray-600">
        <b className="text-heritage-blue">
          {frameLabel(periods[start])} – {frameLabel(periods[end])}
        </b>
        <span className="tabular-nums">
          {' '}
          · {shown} of {count} frame{count === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );
};
